import pRetry from 'p-retry';
import { AdditionalInfoView } from '../../../types';
import { useAdditionalInfosResource } from '../use-additional-infos-event-resource';
import { createEventController } from './create-event-controller';
import { AdditionalInfosController } from './types';

export interface CreateScheduledEventControllerConfig {
    entityId: number;
    resource: ReturnType<typeof useAdditionalInfosResource>;
    pageSize?: number;
    enablePinnedEntries?: boolean;
}

/**
 * Scheduled event controller.
 *
 * Overrides loadPage (different endpoint, always hasNextPage: false) and
 * createFilterDirective (() => true). Delegates createInfo, deleteInfo, and
 * createStreamListeners to the event controller — scheduled events use the
 * same CRUD APIs and the same 'infos' WebSocket stream.
 */
export function createScheduledEventController(
    config: CreateScheduledEventControllerConfig
): AdditionalInfosController {
    const { entityId, resource, pageSize, enablePinnedEntries = false } = config;

    const eventController = createEventController({
        entityId,
        resource,
        pageSize,
        includeEventNarratives: true,
        // Scheduled-event controller never calls the paginated endpoint, so the
        // flag is irrelevant — but the config requires it. Hardcode true to
        // avoid leaking the legacy code path's quirks into shared CRUD/stream
        // logic on the new endpoint code path.
        useNarrativeEndpoints: true,
        // Forward so the shared 'infos' stream listener reconciles the pinned
        // section on pin/unpin nudges (otherwise a pin from another user never
        // reaches this client's pinned section).
        enablePinnedEntries,
    });

    // A scheduled event has a single, always-loaded narrative, so loadPage and
    // getPinnedNarratives read the exact same data. Both bootstrap effects fire
    // on mount; coalesce their concurrent calls into one in-flight request so the
    // narrative isn't fetched twice. The promise is cleared once settled, so later
    // reloads still refetch fresh data.
    type ScheduledInfosResponse = Awaited<ReturnType<typeof resource.getScheduledEventInfos>>;
    let inFlightInfos: Promise<ScheduledInfosResponse> | null = null;
    const fetchScheduledInfos = (): Promise<ScheduledInfosResponse> => {
        if (!inFlightInfos) {
            inFlightInfos = pRetry(
                () => resource.getScheduledEventInfos({ agencyEventId: entityId }),
                { retries: 2 }
            ).finally(() => {
                inFlightInfos = null;
            });
        }
        return inFlightInfos;
    };

    return {
        async loadPage() {
            const response = await fetchScheduledInfos();
            const data = response?.data?.data;
            return {
                additionalInfos: data?.additionalInfos ?? [],
                pagination: {},
                agencyEventNumberToIdMap: data?.agencyEventNumberToIdMap ?? {},
            };
        },

        createInfo: eventController.createInfo,
        deleteInfo: eventController.deleteInfo,

        async editInfo({ text }) {
            if (text == null) {
                throw new Error(
                    `Edit scheduled event narrative requires text for scheduledEventId=${entityId}`
                );
            }
            const result = await resource.editScheduledEventNarrative({
                scheduledEventId: entityId,
                text,
            });
            const partial = result.data.data;
            if (!partial) {
                throw new Error(
                    `Edit scheduled event narrative returned no data for scheduledEventId=${entityId}`
                );
            }
            // The scheduled narrative is keyed on the scheduled event id (it has no
            // additional_info row of its own, and the BE stamps the view's id with
            // this same value). The BE echoes the full resulting text/priority, not
            // just the changed field.
            return {
                id: entityId,
                text: partial.text ?? '',
                priority: partial.priority ?? '',
            };
        },

        async setPriority({ priority }) {
            const result = await resource.setScheduledNarrativePriority({
                scheduledEventId: entityId,
                priority,
            });
            const partial = result.data.data;
            if (!partial) {
                throw new Error(
                    `Emphasize scheduled event narrative returned no data for scheduledEventId=${entityId}`
                );
            }
            // Same keying as editInfo: the scheduled narrative's id is the scheduled
            // event id. The scheduled priority response echoes the full text/priority.
            return {
                id: entityId,
                text: partial.text ?? '',
                priority: partial.priority ?? '',
            };
        },

        // Scheduled events support pin/unpin + emphasize (the full row-action set
        // minus delete). Text edit, emphasize/priority (RND-58567), and pin/unpin
        // each hit their own dedicated scheduled-event endpoint, keyed on the
        // scheduled event id (pin/unpin share one endpoint via the action flag).
        //
        // The pin state lives in the event blob (not additional_info_event_links),
        // so there is no /narrative/pinned equivalent — derive the pinned section
        // from the same loaded narrative(s) that carry isPinned (shares loadPage's
        // in-flight fetch on bootstrap; no extra request).
        async getPinnedNarratives() {
            const response = await fetchScheduledInfos();
            const narratives = (response?.data?.data?.additionalInfos ??
                []) as AdditionalInfoView[];
            return narratives.filter((narrative) => narrative.isPinned);
        },

        async pinNarrative() {
            const response = await resource.pinScheduledEventNarrative({
                scheduledEventId: entityId,
                action: 'PIN',
            });
            const view = response?.data?.data?.additionalInfo as AdditionalInfoView | undefined;
            if (!view) {
                throw new Error(
                    `Pin scheduled event narrative returned no data for scheduledEventId=${entityId}`
                );
            }
            return view;
        },

        async unpinNarrative() {
            const response = await resource.pinScheduledEventNarrative({
                scheduledEventId: entityId,
                action: 'UNPIN',
            });
            const view = response?.data?.data?.additionalInfo as AdditionalInfoView | undefined;
            if (!view) {
                throw new Error(
                    `Unpin scheduled event narrative returned no data for scheduledEventId=${entityId}`
                );
            }
            return view;
        },

        createStreamListeners: eventController.createStreamListeners,

        createFilterDirective() {
            return () => true;
        },
    };
}
